import { useTheme } from "@mui/material";

export const getCalendarPdfUrl = () => {
  if (process.env.NODE_ENV === "development") {
    return "connect_development";
  } else {
    return "connect";
  }
};

/**
 * Calculate dynamic table height based on page elements
 * @param {object} options - Configuration options
 * @param {boolean} options.hasTabs - Whether the page has tabs (adds ~80px)
 * @param {boolean} options.hasHeader - Whether the page has a header section (adds ~80px)
 * @param {boolean} options.hasFilters - Whether the page has filter dropdowns (adds ~60px)
 * @param {number} options.extraOffset - Any additional offset needed
 * @returns {string} - CSS calc() string for height
 */
export const getTableHeight = (options = {}) => {
  const {
    hasTabs = false,
    hasHeader = false,
    hasFilters = false,
    extraOffset = 0,
  } = options;

  // Base offset: navbar (~64px) + page margin (~16px) + padding (~30px) + footer margin (~20px)
  let baseOffset = 130;

  if (hasTabs) baseOffset += 80;      // Tab section height
  if (hasHeader) baseOffset += 70;    // Header with title/subtitle
  if (hasFilters) baseOffset += 50;   // Filter dropdowns row

  baseOffset += extraOffset;

  return `calc(100vh - ${baseOffset}px)`;
};

/**
 * Dashboard style for DataGrid components
 * Now uses dynamic height calculation
 */
export const dashboardStyle = (theme, height) => ({
  paddingLeft: "15px",
  paddingRight: "15px",
  color: "#222836",
  backgroundColor: "#ffffff",
  borderRadius: "15px",
  boxShadow: "13px 22px 19px 0px rgba(0,0,0,0.2)",
  border: "1px solid #DBE2EF",
  fontWeight: "light",
  fontSize: "13px",
  // Use provided height or default to 100%
  height: height || "100%",
  minHeight: "400px", // Minimum height to prevent too small tables
  width: '100%',
  '& .MuiTablePagination-root': {
    color: "#222836",
  },
  '.MuiDataGrid-columnSeparator': {
    display: 'none',
  },
  '&.MuiDataGrid-root': {
    border: 'none',
    backgroundColor: "#ffffff",
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: "#ffffff",
    color: "#04497a",
    borderBottom: "1px solid #DBE2EF",
    borderRadius: "15px 15px 0 0",
  },
  '& .MuiDataGrid-columnHeader': {
    backgroundColor: "#ffffff",
    color: "#04497a",
    marginLeft: '-2px',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 700,
    color: "#04497a",
  },
  '& .MuiDataGrid-cell': {
    padding: '0 10px',
    color: "#222836",
  },
  '& .MuiDataGrid-row': {
    backgroundColor: "#ffffff",
  },
});

/**
 * Wrapper style for consistent page container
 * This ensures all pages have same structure
 */
export const pageContainerStyle = {
  margin: ".5rem 2.5rem",
  height: "calc(100vh - 110px)", // Matches sidebar height (100vh - navbar - margins)
  display: "flex",
  flexDirection: "column",
};

/**
 * Table container style - takes remaining space after headers/tabs
 */
export const tableContainerStyle = {
  flexGrow: 1,
  minHeight: 0, // Important for flex child to shrink properly
  display: "flex",
  flexDirection: "column",
};

export const tabStyles = (theme, selectedTab) => ({
  flexDirection: 'row',
  justifyContent: 'flex-start',
  border: '1px solid #e5e5e5',
  fontWeight: 'bold',
  borderRadius: '15px',
  backgroundColor: (index) => (selectedTab === index ? '#04497a' : 'initial'),
  color: (index) => (selectedTab === index ? 'white' : '#04497a'),
  '&.Mui-selected': {
    backgroundColor: '#04497a',
    color: 'white',
  }
});